import { useEkycPromptStore } from '@/stores/useEkycPromptStore'
import { useSessionStore } from '@/stores/useSessionStore'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/api/query-keys'
import {
  isSignupSurveyInstrument,
  clearSignupSurveyHomeCard,
  setEkycRemindPending,
} from '@/features/signup-survey'
import { captureSpSurveyCompletionCtaClicked } from '@/lib/analytics'
import { buildCompletionReward } from '../lib/survey-completion-reward'
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle'
import { useToast } from '@/shared/toast/ToastProvider'
import { useProviderInstrumentInfo } from '../api/instrument-info-queries'
import { useAbandonSurvey, useSaveSurveyAnswer, useSubmitSurvey } from '../api/survey-execution-mutations'
import { useSurveySession } from '../api/survey-execution-queries'
import {
  canProceedToNext,
  draftAnswerForQuestion,
} from '../lib/survey-answer'
import { parseSurveyDefinition } from '../lib/parse-survey-definition'
import { isBatch1SurveyQuestionType } from '../lib/survey-question.types'
import type { SurveyDraftAnswer } from '../lib/survey-question.types'
import { resolveCurrentQuestion } from '../lib/survey-navigation'
import { resolveRoutingAwareProgress } from '../lib/survey-routing-progress'
import { resolveRemainingEctMinutes } from '../lib/survey-ect'
import {
  parseRoutingRulesFromDefinition,
  answersByQuestionIdFromMap,
  willEndInstrumentAfterAnswer,
} from '../lib/survey-routing-preview'
import { sessionTargetBlockedFromResponse } from '../lib/survey-save-error'
import { useSurveyExecutionSessionGuards } from '../lib/use-survey-execution-session-guards'
import { useSurveyBrowserAbandon } from '../lib/use-survey-browser-abandon'
import { useSurveyExecutionNext } from '../lib/use-survey-execution-next'
import { useSurveyDraftNavigation } from '../lib/use-survey-draft-navigation'
import { useSurveySessionBootstrap } from '../lib/use-survey-session-bootstrap'
import { useSurveyEntryCompletionChrome } from '../lib/SurveyEntryChromeContext'
import { notifyInstrumentUnavailable } from '../lib/handle-survey-session-blocked'
import { isInstrumentNotFoundError, isInstrumentPausedError } from '../lib/instrument-info-state'
import type {
  ProviderReward,
  ProviderSubmitConfirmation,
  SurveyExecutionLocationState,
} from '../lib/types'
import { useSpProfile } from '@/features/rewards/api'
import {
  isRedeemGatedByTenantKyc,
  useSpKycConfiguration,
} from '@/features/kyc-config'
import { applySurveyTenantContext } from '@/lib/survey-tenant-context'
import { getSubdomainFromHostname } from '@/lib/subdomain'
import {
  EKYC_UNDER_REVIEW_BLOCK_MESSAGE,
  isEkycUnderReview,
} from '@/features/ekyc/lib/ekycUi'
import { SurveyExecutionPageView } from './SurveyExecutionPageView'
import {
  isGuestSurveyClaimJourney,
  isPendingSurveyUser,
  persistSurveyFirstOnboardingHandoff,
} from '../lib/pending-survey-session'
import { SignupShareThemeProvider } from '../styles/signupShareTheme'

export function SurveyExecutionPage() {
  const { instrumentId } = useParams<{ instrumentId: string }>()
  const userId = useSessionStore((s) => s.user?.id)
  const userSubdomain = useSessionStore((s) => s.user?.subdomain)
  const userGuest = useSessionStore((s) => isGuestSurveyClaimJourney(s.user))
  const surveyFirstJourney = useSessionStore((s) => isPendingSurveyUser(s.user))
  const openPrerequisites = useEkycPromptStore((s) => s.openPrerequisites)
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const state = location.state as SurveyExecutionLocationState | null
  const { data: profile } = useSpProfile()
  const underReview = isEkycUnderReview(profile?.ekycStatus)

  const responseId = state?.responseId
  const initialDefinition = state?.definition

  const infoQuery = useProviderInstrumentInfo(instrumentId)
  const sessionQuery = useSurveySession({
    instrumentId,
    responseId,
    initialDefinition,
    enabled: Boolean(instrumentId && responseId),
  })

  const saveMutation = useSaveSurveyAnswer()
  const submitMutation = useSubmitSurvey()
  const abandonMutation = useAbandonSurvey()
  const { showToast } = useToast()

  useSurveyBrowserAbandon(responseId, abandonMutation)

  const [answerMap, setAnswerMap] = useState<Map<string, SurveyDraftAnswer>>(new Map())
  const [visitedQuestionIds, setVisitedQuestionIds] = useState<string[]>([])
  const [localQuestionId, setLocalQuestionId] = useState<string | null>(null)
  const [submitSuccessModalOpen, setSubmitSuccessModalOpen] = useState(false)
  const [showCompletionScreen, setShowCompletionScreen] = useState(false)
  const [submitConfirmation, setSubmitConfirmation] = useState<ProviderSubmitConfirmation | null>(
    null,
  )
  const [completionReward, setCompletionReward] = useState<ProviderReward | null>(null)
  const [showRequiredError, setShowRequiredError] = useState(false)
  const [sessionInitialized, setSessionInitialized] = useState(false)
  const [targetBlocked, setTargetBlocked] = useState(false)
  const questionEnteredAtRef = useRef(Date.now())

  const session = sessionQuery.data
  const response = session?.response
  const info = infoQuery.data

  const { timeoutHandledRef, handleSessionFailure } = useSurveyExecutionSessionGuards(
    response,
    showToast,
    navigate,
    setTargetBlocked,
  )

  useEffect(() => {
    if (sessionQuery.error && isInstrumentPausedError(sessionQuery.error)) {
      notifyInstrumentUnavailable(showToast, navigate)
    }
  }, [sessionQuery.error, showToast, navigate])

  const definition = useMemo(() => {
    if (!session?.definition || !instrumentId) return null
    return parseSurveyDefinition(
      session.definition,
      instrumentId,
      response?.campaignId ?? '',
    )
  }, [session?.definition, instrumentId, response?.campaignId])

  useEffect(() => {
    if (!definition) return
    const tenantSubdomain =
      definition.tenantDomainPrefix?.trim() ||
      userSubdomain?.trim() ||
      (globalThis.window !== undefined
        ? getSubdomainFromHostname(globalThis.window.location.hostname)
        : '') ||
      ''
    const tenantId = definition.tenantId?.trim() ?? ''
    if (!tenantId && !tenantSubdomain) return
    applySurveyTenantContext({ tenantId, tenantSubdomain })
  }, [definition, userSubdomain])

  const currentQuestionId =
    localQuestionId ?? response?.currentQuestionId ?? definition?.questions[0]?.id ?? null

  const currentQuestion = useMemo(() => {
    if (!definition) return null
    return resolveCurrentQuestion(definition, currentQuestionId)
  }, [definition, currentQuestionId])

  const surveyTitle = info?.name ?? 'Survey'
  const surveyDescription = info?.description?.trim() || null
  const instrumentEstimatedMinutes = info?.estimatedCompletionMinutes ?? null

  useDocumentTitle(currentQuestion?.prompt?.slice(0, 40) ?? surveyTitle)

  useSurveySessionBootstrap(
    response,
    definition,
    sessionInitialized,
    setSessionInitialized,
    setAnswerMap,
    setVisitedQuestionIds,
    setLocalQuestionId,
  )

  useEffect(() => {
    setShowRequiredError(false)
  }, [currentQuestionId])

  useEffect(() => {
    questionEnteredAtRef.current = Date.now()
  }, [currentQuestionId])

  const draftAnswer: SurveyDraftAnswer = currentQuestion
    ? draftAnswerForQuestion(answerMap, currentQuestion.id)
    : null

  const routingRules = useMemo(
    () => parseRoutingRulesFromDefinition(session?.definition),
    [session?.definition],
  )

  const hasRoutingRules = routingRules.length > 0

  const isLastQuestion = useMemo(() => {
    if (!definition || !currentQuestion) return false
    return willEndInstrumentAfterAnswer(
      definition,
      routingRules,
      answerMap,
      currentQuestion,
      draftAnswer,
    )
  }, [definition, routingRules, answerMap, currentQuestion, draftAnswer])

  const answersByQuestionId = useMemo(() => {
    if (!definition || !currentQuestion) return undefined
    return answersByQuestionIdFromMap(
      definition,
      answerMap,
      currentQuestion.id,
      draftAnswer,
    )
  }, [definition, currentQuestion, answerMap, draftAnswer])

  const { questionIndex, totalQuestions } = useMemo(
    () =>
      resolveRoutingAwareProgress({
        routingPath: visitedQuestionIds,
        currentQuestionId: currentQuestion?.id ?? null,
        definition,
        hasRoutingRules,
        routingRules: hasRoutingRules ? routingRules : undefined,
        answersByQuestionId: hasRoutingRules ? answersByQuestionId : undefined,
        endsAtCurrentQuestion: isLastQuestion,
      }),
    [
      visitedQuestionIds,
      currentQuestion?.id,
      definition,
      hasRoutingRules,
      routingRules,
      answersByQuestionId,
      isLastQuestion,
    ],
  )

  const estimatedMinutes = useMemo(
    () =>
      resolveRemainingEctMinutes({
        definition,
        routingPath: visitedQuestionIds,
        currentQuestionId: currentQuestion?.id ?? null,
        hasRoutingRules,
        routingRules: hasRoutingRules ? routingRules : undefined,
        answersByQuestionId: hasRoutingRules ? answersByQuestionId : undefined,
        fallbackMinutes: instrumentEstimatedMinutes,
        endsAtCurrentQuestion: isLastQuestion,
      }),
    [
      definition,
      visitedQuestionIds,
      currentQuestion?.id,
      hasRoutingRules,
      routingRules,
      answersByQuestionId,
      instrumentEstimatedMinutes,
      isLastQuestion,
    ],
  )

  const supportsType =
    currentQuestion != null && isBatch1SurveyQuestionType(currentQuestion.type)

  const canProceed = currentQuestion
    ? canProceedToNext(currentQuestion, draftAnswer)
    : false

  const targetReachedLive = response
    ? sessionTargetBlockedFromResponse(response)
    : false

  const nextDisabled = !supportsType || !canProceed || targetReachedLive

  const { handleDraftChange, handleBack } = useSurveyDraftNavigation({
    currentQuestion,
    visitedQuestionIds,
    instrumentId,
    navigate,
    setAnswerMap,
    setVisitedQuestionIds,
    setLocalQuestionId,
    setShowRequiredError,
  })

  const handleNext = useSurveyExecutionNext({
    instrumentId,
    responseId,
    currentQuestion,
    definition,
    draftAnswer,
    answerMap,
    routingRules,
    visitedQuestionIds,
    questionEnteredAt: questionEnteredAtRef.current,
    session,
    info,
    saveMutation,
    submitMutation,
    queryClient,
    showToast,
    navigate,
    timeoutHandledRef,
    setAnswerMap,
    setLocalQuestionId,
    setVisitedQuestionIds,
    setSubmitConfirmation,
    setCompletionReward,
    setSubmitSuccessModalOpen,
    setShowRequiredError,
    setTargetBlocked,
    handleSessionFailure,
  })

  const handleNextGuarded = useCallback(async () => {
    if (underReview) {
      showToast(EKYC_UNDER_REVIEW_BLOCK_MESSAGE, 'error')
      return
    }
    await handleNext()
  }, [handleNext, showToast, underReview])

  // Sticky for this page visit — once detected as signup survey, keep signup copy
  // even if prefs are cleared mid-flow.
  const [signupSurveyCompletion, setSignupSurveyCompletion] = useState(() =>
    Boolean(userId && instrumentId && isSignupSurveyInstrument(userId, instrumentId)),
  )
  useEffect(() => {
    if (userId && instrumentId && isSignupSurveyInstrument(userId, instrumentId)) {
      setSignupSurveyCompletion(true)
    }
  }, [userId, instrumentId])

  const isSignupSurveyCompletion = signupSurveyCompletion

  const isReturnAfterSubmit =
    response?.status === 'submitted' &&
    !submitSuccessModalOpen &&
    !showCompletionScreen

  const shouldFetchKycConfig =
    !userGuest &&
    isSignupSurveyCompletion &&
    (showCompletionScreen || isReturnAfterSubmit)
  const { data: tenantKycMode } = useSpKycConfiguration(shouldFetchKycConfig)
  const signupSurveyEkycGated =
    !isSignupSurveyCompletion ||
    isRedeemGatedByTenantKyc(tenantKycMode ?? 'unknown')

  const handleSubmitSuccessConfirm = useCallback(() => {
    setSubmitSuccessModalOpen(false)
    setShowCompletionScreen(true)
    // Clears Home card cache but keeps instrument_id so signup CTAs still show.
    if (userId && instrumentId && isSignupSurveyInstrument(userId, instrumentId)) {
      clearSignupSurveyHomeCard(queryClient, userId)
      setSignupSurveyCompletion(true)
    }
    if (!instrumentId || !responseId) return
    queryClient.setQueryData(
      queryKeys.spSurvey.session(instrumentId, responseId),
      (prev: typeof session | undefined) => {
        if (!prev) return prev
        return {
          ...prev,
          response: {
            ...prev.response,
            status: 'submitted',
            currentQuestionId: null,
          },
        }
      },
    )
  }, [userId, instrumentId, responseId, queryClient])

  const handleStartEkyc = useCallback(() => {
    if (userId) {
      clearSignupSurveyHomeCard(queryClient, userId)
      setEkycRemindPending(userId, false)
    }
    // Skip Verify modal — go straight to I understand + eKYC session.
    openPrerequisites()
    navigate('/')
  }, [userId, queryClient, openPrerequisites, navigate])

  const handleRemindLater = useCallback(() => {
    if (userId) {
      clearSignupSurveyHomeCard(queryClient, userId)
      const reward = buildCompletionReward(info ?? null, submitConfirmation, completionReward)
      setEkycRemindPending(userId, true, reward.display ?? null)
    }
    navigate('/')
  }, [userId, queryClient, navigate, info, submitConfirmation, completionReward])

  const handleCreateAccount = useCallback(() => {
    persistSurveyFirstOnboardingHandoff(userId, instrumentId)
    if (surveyFirstJourney) {
      navigate('/instrument/onboarding?surveyFirst=1')
      return
    }
    navigate('/instrument/onboarding/account')
  }, [instrumentId, navigate, surveyFirstJourney, userId])

  const handleClaimNow = useCallback(() => {
    if (instrumentId) {
      const reward = buildCompletionReward(info ?? null, submitConfirmation, completionReward)
      captureSpSurveyCompletionCtaClicked({
        cta_selected: 'claim_reward',
        instrument_id: instrumentId,
        reward_amount_usd: reward.amount ?? 0,
      })
    }
    navigate('/rewards')
  }, [navigate, instrumentId, info, submitConfirmation, completionReward])

  const handleCompletionHome = useCallback(() => {
    if (instrumentId) {
      const reward = buildCompletionReward(info ?? null, submitConfirmation, completionReward)
      captureSpSurveyCompletionCtaClicked({
        cta_selected: 'go_home',
        instrument_id: instrumentId,
        reward_amount_usd: reward.amount ?? 0,
      })
    }
    navigate('/')
  }, [navigate, instrumentId, info, submitConfirmation, completionReward])

  const showingCompletionScreen =
    Boolean(info) && (showCompletionScreen || isReturnAfterSubmit)

  useSurveyEntryCompletionChrome(showingCompletionScreen)

  if (infoQuery.isError && isInstrumentNotFoundError(infoQuery.error)) {
    return <Navigate to="/" replace />
  }

  const nextPending = saveMutation.isPending || submitMutation.isPending

  const view = (
    <SurveyExecutionPageView
      instrumentId={instrumentId}
      responseId={responseId}
      targetBlocked={targetBlocked}
      sessionLoading={sessionQuery.isLoading}
      infoLoading={infoQuery.isLoading}
      sessionError={sessionQuery.isError}
      definition={definition}
      currentQuestion={currentQuestion}
      surveyTitle={surveyTitle}
      surveyDescription={surveyDescription}
      estimatedMinutes={estimatedMinutes}
      questionIndex={questionIndex}
      totalQuestions={totalQuestions}
      showRequiredError={showRequiredError}
      draftAnswer={draftAnswer}
      nextDisabled={nextDisabled}
      nextPending={nextPending}
      isLastQuestion={isLastQuestion}
      targetReachedLive={targetReachedLive}
      showCompletionScreen={showCompletionScreen}
      isReturnAfterSubmit={isReturnAfterSubmit}
      info={info}
      submitConfirmation={submitConfirmation}
      completionReward={completionReward}
      submitSuccessModalOpen={submitSuccessModalOpen}
      onNavigateInfo={() => navigate(`/instrument/${instrumentId}/info`)}
      onDraftChange={handleDraftChange}
      onBack={handleBack}
      onNext={() => {
        handleNextGuarded().catch(() => undefined)
      }}
      onSubmitSuccessConfirm={handleSubmitSuccessConfirm}
      onClaimNow={handleClaimNow}
      onCompletionHome={handleCompletionHome}
      signupSurveyMode={isSignupSurveyCompletion}
      signupSurveyEkycGated={signupSurveyEkycGated}
      onStartEkyc={handleStartEkyc}
      onRemindLater={handleRemindLater}
      webSurveySignupMode={userGuest}
      surveyFirstJourney={surveyFirstJourney}
      onCreateAccount={handleCreateAccount}
    />
  )

  if (userGuest || surveyFirstJourney) {
    return <SignupShareThemeProvider>{view}</SignupShareThemeProvider>
  }
  return view
}
